/**
 * 🔧 Inicialización de Métodos de Ecommerce
 * 
 * Crea automáticamente los métodos de pago y entrega por defecto
 * basados en la configuración cuando no existen en la base de datos.
 */

import { supabase } from './supabase';
import { PUBLIC_SERVICE_MODES } from '@/config/ecommerce.config';
import { devLog } from '@/lib/utils';

/**
 * Inicializa los métodos de envío (shipping) por defecto para un tenant
 */
export async function initializeShippingMethods(tenantId: string): Promise<void> {
  try {
    const { data: existing, error: checkError } = await supabase
      .from('ecommerce_shipping_methods')
      .select('id')
      .eq('tenant_id', tenantId)
      .limit(1);

    if (checkError) throw checkError;

    if (existing && existing.length > 0) return;

    // Defaults estilo Shopify (puedes editarlos luego en configuración)
    const shippingToInsert = [
      {
        tenant_id: tenantId,
        name: 'Envío Regular',
        region_label: 'Lima Metropolitana',
        description: 'Rango despacho: 3 a 8 pm',
        price: 7.9,
        currency_code: 'PEN',
        enabled: true,
        display_order: 0,
      },
      {
        tenant_id: tenantId,
        name: 'Envío Perú | Shalom',
        region_label: null,
        description: 'Nombre agencia Shalom',
        price: 10.0,
        currency_code: 'PEN',
        enabled: true,
        display_order: 1,
      },
    ];

    const { error: insertError } = await supabase.from('ecommerce_shipping_methods').insert(shippingToInsert);
    if (insertError) throw insertError;

    devLog.warn('✅ Métodos de envío inicializados para tenant:', tenantId);
  } catch (error: unknown) {
    devLog.error('❌ Error al inicializar métodos de envío:', error);
    throw error;
  }
}

/**
 * Inicializa los métodos de entrega por defecto para un tenant
 */
export async function initializeDeliveryMethods(tenantId: string): Promise<void> {
  try {
    // Verificar si ya existen métodos para este tenant
    const { data: existing, error: checkError } = await supabase
      .from('ecommerce_delivery_methods')
      .select('id')
      .eq('tenant_id', tenantId)
      .limit(1);

    if (checkError) throw checkError;

    // Si ya existen métodos, no hacer nada
    if (existing && existing.length > 0) {
      return;
    }

    // Crear métodos de entrega por defecto
    const deliveryMethodsToInsert = PUBLIC_SERVICE_MODES.map((method, index) => ({
      tenant_id: tenantId,
      name: method.label, // Ya incluye "Retiro" en lugar de "Recojo en el local"
      description: method.description || null,
      icon_url: null, // Se pueden agregar iconos después
      enabled: method.enabled,
      display_order: index,
    }));

    const { error: insertError } = await supabase
      .from('ecommerce_delivery_methods')
      .insert(deliveryMethodsToInsert);

    if (insertError) throw insertError;

    devLog.warn('✅ Métodos de entrega inicializados para tenant:', tenantId);
  } catch (error: unknown) {
    devLog.error('❌ Error al inicializar métodos de entrega:', error);
    throw error;
  }
}
